import { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import AdminLayout from '@/components/layout/AdminLayout'
import { useNavigate } from 'react-router-dom'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface Listing {
  id: string
  title: string
  status: string
  nfcUid?: string
}

type WriteState = 'idle' | 'waiting' | 'success' | 'error'

interface DiagnosticResult {
  atr: string
  protocol: string
  uid: string
  detectedChipType: string
  isCompatible: boolean
  recommendation: string
  supportsNtag424Application: boolean
  versionInfo?: {
    rawData: string
    vendorId: string
    chipType: string
    hardwareInfo: string
    storageSize: string
    isNtag424Dna: boolean
  }
  tests: Array<{
    testName: string
    passed: boolean
    details: string
    statusWord?: string
    responseData?: string
    responseLength?: number
    commandSent?: string
  }>
}

export default function UsbNfcWriterPage() {
  const navigate = useNavigate()
  const [listings, setListings] = useState<Listing[]>([])
  const [selectedListing, setSelectedListing] = useState<string>('')
  const [writeState, setWriteState] = useState<WriteState>('idle')
  const [message, setMessage] = useState('')
  const [readerAvailable, setReaderAvailable] = useState(false)
  const [loading, setLoading] = useState(false)
  const [enableSunMode, setEnableSunMode] = useState(false)
  const [diagnosing, setDiagnosing] = useState(false)
  const [diagnosticResult, setDiagnosticResult] = useState<DiagnosticResult | null>(null)
  const [diagnosticError, setDiagnosticError] = useState<string | null>(null)

  useEffect(() => {
    loadListings()
    checkReaderStatus()
  }, [])

  const loadListings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/listings?size=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      const data = await response.json()

      // Map the response to our Listing interface
      const fetchedListings: Listing[] = data.content?.map((item: any) => ({
        id: item.id,
        title: item.title,
        status: item.status,
        nfcUid: item.nfcUid
      })) || []

      setListings(fetchedListings)
    } catch (error) {
      console.error('Failed to load listings:', error)
      setListings([])
    }
  }

  const checkReaderStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/readers/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      const data = await response.json()
      setReaderAvailable(data.available)
    } catch (error) {
      console.error('Failed to check reader status:', error)
      setReaderAvailable(false)
    }
  }

  const handleWrite = async () => {
    if (!selectedListing) {
      setMessage('Please select a listing')
      setWriteState('error')
      return
    }

    try {
      setWriteState('waiting')
      setMessage('Place NFC tag on USB reader...')
      setLoading(true)

      const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/write-usb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          listingId: selectedListing,
          enableSunMode: enableSunMode
        })
      })

      const data = await response.json()

      if (data.success) {
        setWriteState('success')
        setMessage(`Success! Tag ${data.displayUid} bound to listing`)
        // Refresh listings
        loadListings()
      } else {
        setWriteState('error')
        setMessage(data.message || 'Failed to write NFC tag')
      }

    } catch (error: any) {
      setWriteState('error')
      setMessage(error.message || 'Failed to write NFC tag')
    } finally {
      setLoading(false)
      // Reset after 3 seconds
      setTimeout(() => {
        setWriteState('idle')
        setMessage('')
      }, 3000)
    }
  }

  const handleDiagnose = async () => {
    try {
      setDiagnosing(true)
      setDiagnosticError(null)
      setDiagnosticResult(null)

      const response = await fetch(`${API_BASE_URL}/api/admin/nfc/tags/diagnose`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      })

      const data = await response.json()

      if (data.success && data.diagnostics) {
        setDiagnosticResult(data.diagnostics)
      } else {
        setDiagnosticError(data.message || 'Failed to diagnose tag')
      }

    } catch (error: any) {
      setDiagnosticError(error.message || 'Failed to diagnose tag')
    } finally {
      setDiagnosing(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-secondary)' }}>
        <div className="max-w-4xl mx-auto" style={{ padding: 'var(--spacing-2xl)' }}>

          {/* Header */}
          <div className="card shimmer" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-xl)' }}>
            <div className="flex items-center" style={{ gap: 'var(--spacing-lg)' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center floating-animation" style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                boxShadow: '0 8px 25px rgba(11, 77, 42, 0.3)'
              }}>
                <svg className="w-8 h-8" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold gradient-text">USB NFC Tag Writer</h1>
                <p style={{ color: 'var(--text-secondary)' }}>Write and bind NFC tags using USB reader</p>
              </div>
            </div>
          </div>

          {/* Reader Status */}
          <div className="card" style={{ padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-lg)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                <div className={`w-3 h-3 rounded-full ${readerAvailable ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="font-medium">
                  {readerAvailable ? 'USB Reader Connected' : 'No USB Reader Detected'}
                </span>
              </div>
              <Button onClick={checkReaderStatus} variant="outline" size="sm">
                Refresh
              </Button>
            </div>
            {!readerAvailable && (
              <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
                Please connect a USB NFC reader (ACR122U, Dioche, etc.) and refresh
              </p>
            )}
          </div>

          {/* Tag Diagnostics */}
          <div className="card shimmer" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-lg)', borderLeft: '4px solid var(--brand-primary)' }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  🔍 NFC Tag Diagnostics
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Verify if your tag is a genuine NTAG 424 DNA chip
                </p>
              </div>
              <Button
                onClick={handleDiagnose}
                disabled={!readerAvailable || diagnosing}
                variant="outline"
                style={{
                  background: diagnosing ? 'var(--background-secondary)' : 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                {diagnosing ? (
                  <span className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Scanning...
                  </span>
                ) : (
                  <span className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Test Tag
                  </span>
                )}
              </Button>
            </div>

            {diagnosing && (
              <div className="text-center py-8">
                <p className="text-blue-600 font-medium">Place your NFC tag on the reader now...</p>
              </div>
            )}

            {diagnosticError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                  <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-red-800">Diagnostic Failed</h3>
                    <p className="text-red-700 text-sm mt-1">{diagnosticError}</p>
                  </div>
                </div>
              </div>
            )}

            {diagnosticResult && (
              <div className="space-y-4">
                {/* Compatibility Status */}
                <div className={`p-4 rounded-lg border-2 ${
                  diagnosticResult.isCompatible
                    ? 'bg-green-50 border-green-300'
                    : 'bg-orange-50 border-orange-300'
                }`}>
                  <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                    {diagnosticResult.isCompatible ? (
                      <svg className="w-6 h-6 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-orange-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg ${diagnosticResult.isCompatible ? 'text-green-800' : 'text-orange-800'}`}>
                        {diagnosticResult.detectedChipType}
                      </h3>
                      <p className={`text-sm mt-1 ${diagnosticResult.isCompatible ? 'text-green-700' : 'text-orange-700'}`}>
                        {diagnosticResult.recommendation}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--background-secondary)' }}>
                    <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>UID</p>
                    <p className="font-mono text-sm mt-1" style={{ color: 'var(--text-primary)' }}>{diagnosticResult.uid}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--background-secondary)' }}>
                    <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Protocol</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>{diagnosticResult.protocol}</p>
                  </div>
                </div>

                {/* Version Info */}
                {diagnosticResult.versionInfo && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--background-secondary)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Version Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--text-muted)' }}>Vendor ID:</span>
                        <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{diagnosticResult.versionInfo.vendorId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--text-muted)' }}>Chip Type:</span>
                        <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{diagnosticResult.versionInfo.chipType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--text-muted)' }}>Hardware:</span>
                        <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{diagnosticResult.versionInfo.hardwareInfo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--text-muted)' }}>Storage:</span>
                        <span style={{ color: 'var(--text-primary)' }}>{diagnosticResult.versionInfo.storageSize}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Test Results */}
                <details className="p-4 rounded-lg cursor-pointer" style={{ backgroundColor: 'var(--background-secondary)' }}>
                  <summary className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    📋 Raw Diagnostic Data ({diagnosticResult.tests.filter(t =>
                      t.passed ||
                      (t.testName === 'GET_VERSION (0x00 0x60 - ISO)' && !t.passed && t.statusWord === '6D00') ||
                      (t.testName === 'SELECT NTAG 424 DNA Application (native 90 5A probe)' && !t.passed && t.statusWord === '911C') ||
                      (t.testName === 'Direct Memory Read (NTAG 21x probe)' && !t.passed && t.statusWord === 'ERROR' && t.details?.includes('Not supported'))
                    ).length}/{diagnosticResult.tests.length} passed)
                  </summary>
                  <div className="mt-4 space-y-4">
                    {diagnosticResult.tests.map((test, idx) => {
                      // Special handling for expected failures that are actually good signs
                      const isExpectedIsoFailure = test.testName === 'GET_VERSION (0x00 0x60 - ISO)' && !test.passed && test.statusWord === '6D00'
                      const isExpectedNativeFailure = test.testName === 'SELECT NTAG 424 DNA Application (native 90 5A probe)' && !test.passed && test.statusWord === '911C'
                      const isExpectedMemoryReadFailure = test.testName === 'Direct Memory Read (NTAG 21x probe)' && !test.passed && test.statusWord === 'ERROR' && test.details?.includes('Not supported')
                      const displayAsPassed = test.passed || isExpectedIsoFailure || isExpectedNativeFailure || isExpectedMemoryReadFailure

                      return (
                        <div key={idx} className="p-3 rounded border" style={{
                          borderColor: displayAsPassed ? '#d1fae5' : '#fecaca',
                          backgroundColor: displayAsPassed ? '#f0fdf4' : '#fef2f2'
                        }}>
                          <div className="flex items-start" style={{ gap: 'var(--spacing-sm)' }}>
                            {displayAsPassed ? (
                              <span className="text-green-600 font-bold">✓</span>
                            ) : (
                              <span className="text-red-600 font-bold">✗</span>
                            )}

                          <div className="flex-1">
                            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                              {test.testName === 'GET_VERSION (0x00 0x60 - ISO)' && !test.passed && test.statusWord === '6D00'
                                ? 'GET_VERSION (ISO 00 60) → not supported (expected for NTAG 424 DNA)'
                                : test.testName === 'SELECT NTAG 424 DNA Application (native 90 5A probe)' && !test.passed && test.statusWord === '911C'
                                ? 'SELECT NTAG 424 DNA (native 90 5A) → not supported in T4T mode (expected)'
                                : test.testName === 'Direct Memory Read (NTAG 21x probe)' && !test.passed && test.statusWord === 'ERROR' && test.details?.includes('Not supported')
                                ? 'Direct Memory Read → not supported (expected for NTAG 424 DNA)'
                                : test.testName}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                              {test.details}
                            </p>

                            {/* Raw Data Section */}
                            {test.commandSent && (
                              <div className="mt-2 space-y-1">
                                <div className="flex items-start text-xs">
                                  <span className="font-semibold mr-2" style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>
                                    Command Sent:
                                  </span>
                                  <code className="font-mono bg-gray-100 px-2 py-0.5 rounded" style={{ color: '#1e40af' }}>
                                    {test.commandSent}
                                  </code>
                                </div>
                                <div className="flex items-start text-xs">
                                  <span className="font-semibold mr-2" style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>
                                    Status Word:
                                  </span>
                                  <code className="font-mono bg-gray-100 px-2 py-0.5 rounded" style={{
                                    color: test.statusWord === '9000' || test.statusWord === '9100' ? '#059669' : '#dc2626'
                                  }}>
                                    {test.statusWord || 'N/A'}
                                  </code>
                                </div>
                                <div className="flex items-start text-xs">
                                  <span className="font-semibold mr-2" style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>
                                    Response Length:
                                  </span>
                                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                                    {test.responseLength !== undefined ? `${test.responseLength} bytes` : 'N/A'}
                                  </span>
                                </div>
                                {test.responseData && test.responseData.length > 0 && (
                                  <div className="flex items-start text-xs">
                                    <span className="font-semibold mr-2" style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>
                                      Response Data:
                                    </span>
                                    <code className="font-mono bg-gray-100 px-2 py-0.5 rounded break-all" style={{ color: '#1e40af', fontSize: '11px' }}>
                                      {test.responseData}
                                    </code>
                                  </div>
                                )}
                                {(!test.responseData || test.responseData.length === 0) && (
                                  <div className="flex items-start text-xs">
                                    <span className="font-semibold mr-2" style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>
                                      Response Data:
                                    </span>
                                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      (empty)
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      )
                    })}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs" style={{ color: '#1e40af' }}>
                    <p className="font-semibold mb-1">💡 Understanding the data:</p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li><strong>Command Sent:</strong> The raw APDU command bytes sent to the chip</li>
                      <li><strong>Status Word (SW):</strong> 9000 = Success, 917E = Wrong length, 6D00 = Instruction not supported, 911C = Illegal command</li>
                      <li><strong>Response Data:</strong> The actual bytes returned by the chip (if any)</li>
                      <li><strong>Expected "failures" for genuine NTAG 424 DNA:</strong>
                        <ul className="ml-4 mt-1">
                          <li>ISO GET_VERSION (00 60) → 6D00: Only responds to native commands</li>
                          <li>Native SELECT (90 5A) → 911C: Disabled in default Type 4 Tag mode</li>
                          <li>Direct Memory Read (FF B0) → ERROR: Uses file system instead of direct memory access</li>
                        </ul>
                      </li>
                      <li><strong>All three "failures" indicate proper NTAG 424 DNA behavior!</strong></li>
                    </ul>
                  </div>
                </details>
              </div>
            )}

            {!diagnosing && !diagnosticResult && !diagnosticError && (
              <div className="text-center py-4" style={{ color: 'var(--text-muted)' }}>
                <p className="text-sm">Click "Test Tag" to identify your NFC chip</p>
              </div>
            )}
          </div>

          {/* Write Form */}
          <div className="card" style={{ padding: 'var(--spacing-2xl)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Write NFC Tag
            </h2>

            {/* Listing Selection */}
            <div style={{ marginBottom: 'var(--spacing-xl)' }}>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Select Listing
              </label>
              <select
                value={selectedListing}
                onChange={(e) => setSelectedListing(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border"
                style={{
                  borderColor: 'var(--border-primary)',
                  backgroundColor: 'var(--background-primary)',
                  color: 'var(--text-primary)'
                }}
                disabled={loading}
              >
                <option value="">-- Select a listing --</option>
                {listings.map((listing) => (
                  <option key={listing.id} value={listing.id}>
                    {listing.title} {listing.nfcUid ? `(Already bound: ${listing.nfcUid})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* SUN Mode Toggle */}
            <div style={{ marginBottom: 'var(--spacing-lg)', padding: 'var(--spacing-md)', backgroundColor: 'var(--background-secondary)', borderRadius: '8px', border: '1px solid var(--border-primary)' }}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableSunMode}
                  onChange={(e) => setEnableSunMode(e.target.checked)}
                  disabled={loading}
                  className="w-5 h-5 cursor-pointer"
                  style={{ accentColor: 'var(--brand-primary)' }}
                />
                <div>
                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    🔒 Enable NTAG 424 DNA Anti-Cloning Security (SUN Mode)
                  </span>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Configures tag with AES-128 encryption and CMAC verification for maximum security.
                    <br />
                    <span className="font-medium text-orange-600">⚠️ Only use with genuine NTAG 424 DNA tags!</span>
                    <br />
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Configuration takes 3-5 seconds. Tag must be factory-new or reset to factory defaults.
                    </span>
                  </p>
                </div>
              </label>
            </div>

            {/* Status Messages */}
            {message && (
              <div className={`shimmer mb-4 p-4 rounded-lg ${
                writeState === 'success' ? 'bg-green-50 border border-green-200' :
                writeState === 'error' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                  {writeState === 'waiting' && (
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {writeState === 'success' && (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {writeState === 'error' && (
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <p className={`font-medium ${
                    writeState === 'success' ? 'text-green-700' :
                    writeState === 'error' ? 'text-red-700' :
                    'text-blue-700'
                  }`}>
                    {message}
                  </p>
                </div>
              </div>
            )}

            {/* Write Button */}
            <Button
              onClick={handleWrite}
              disabled={!selectedListing || loading || !readerAvailable}
              className="w-full shimmer"
              style={{
                padding: 'var(--spacing-lg)',
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center" style={{ gap: 'var(--spacing-md)' }}>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Waiting for Tag...
                </span>
              ) : (
                <span className="flex items-center justify-center" style={{ gap: 'var(--spacing-md)' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  Write NFC Tag
                </span>
              )}
            </Button>

            {/* Instructions */}
            <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--background-secondary)' }}>
              <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Instructions:</h3>
              <ol className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li>1. Select the listing you want to bind to the NFC tag</li>
                <li>2. Enable SUN Mode for high-value collectibles (requires NTAG 424 DNA)</li>
                <li>3. Click "Write NFC Tag" button</li>
                <li>4. Place your NFC tag on the USB reader within 30 seconds</li>
                <li>5. Wait for confirmation message (SUN mode takes 3-5 seconds)</li>
              </ol>
              {enableSunMode && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs" style={{ color: '#1e40af' }}>
                  <strong>SUN Mode Enabled:</strong> Make sure you're using a factory-new NTAG 424 DNA tag.
                  Regular NFC tags (NTAG213/215/216) will fail with SUN mode enabled.
                </div>
              )}
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-4">
            <Button onClick={() => navigate('/admin')} variant="outline">
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
